import React, { useId, useRef, useState } from 'react';
import type { Language } from '../types';
import { getTranslation } from '../utils/translations';
import { isAllowedRecipeImageFile } from '../utils/recipeImageFile';
import CameraIcon from './icons/CameraIcon';
import GlobeIcon from './icons/GlobeIcon';
import TrashIcon from './icons/TrashIcon';
import XIcon from './icons/XIcon';

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif'];

type SourceModalStep = 'choices' | 'url';

interface RecipeImagePickerProps {
    galleryUrls?: string[];
    previewUrl?: string;
    onFileSelected: (file: File) => void;
    onUrlSelected: (url: string) => void;
    onRemoveAtIndex?: (index: number) => void;
    onClear?: () => void;
    isUploading?: boolean;
    disabled?: boolean;
    language?: Language;
    className?: string;
}

const RecipeImagePicker: React.FC<RecipeImagePickerProps> = ({
    galleryUrls,
    previewUrl,
    onFileSelected,
    onUrlSelected,
    onRemoveAtIndex,
    onClear,
    isUploading = false,
    disabled = false,
    language,
    className = '',
}) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);
    const galleryInputId = useId();
    const cameraInputId = useId();
    const galleryInputRef = useRef<HTMLInputElement>(null);
    const cameraInputRef = useRef<HTMLInputElement>(null);
    const [localError, setLocalError] = useState<string | null>(null);
    const [isSourceModalOpen, setIsSourceModalOpen] = useState(false);
    const [sourceModalStep, setSourceModalStep] = useState<SourceModalStep>('choices');
    const [urlDraft, setUrlDraft] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);

    const resolvedGalleryUrls = galleryUrls?.length
        ? galleryUrls
        : previewUrl
            ? [previewUrl]
            : [];

    const activePreviewUrl = resolvedGalleryUrls[selectedIndex] ?? resolvedGalleryUrls[0];
    const hasImages = resolvedGalleryUrls.length > 0;

    const closeSourceModal = () => {
        setIsSourceModalOpen(false);
        setSourceModalStep('choices');
        setUrlDraft('');
        setLocalError(null);
    };

    const openSourceModal = () => {
        if (disabled || isUploading) return;
        setSourceModalStep('choices');
        setUrlDraft('');
        setLocalError(null);
        setIsSourceModalOpen(true);
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;

        if (!isAllowedRecipeImageFile(file)) {
            setLocalError(t('recipePhotoInvalidType'));
            return;
        }

        setLocalError(null);
        onFileSelected(file);
        closeSourceModal();
    };

    const openGallery = () => {
        closeSourceModal();
        window.setTimeout(() => galleryInputRef.current?.click(), 0);
    };

    const openCamera = () => {
        closeSourceModal();
        window.setTimeout(() => cameraInputRef.current?.click(), 0);
    };

    const openUrlStep = () => {
        setSourceModalStep('url');
        setLocalError(null);
    };

    const confirmUrl = () => {
        const trimmed = urlDraft.trim();
        if (!trimmed) {
            setLocalError(t('recipePhotoUrlRequired'));
            return;
        }

        try {
            const parsed = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
            if (!['http:', 'https:'].includes(parsed.protocol)) {
                throw new Error('invalid');
            }
        } catch {
            setLocalError(t('recipePhotoUrlInvalid'));
            return;
        }

        setLocalError(null);
        onUrlSelected(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
        closeSourceModal();
    };

    const handleRemoveSelected = () => {
        if (!hasImages) return;
        if (onRemoveAtIndex) {
            onRemoveAtIndex(selectedIndex);
            setSelectedIndex((current) => Math.max(0, current - 1));
            return;
        }
        onClear?.();
        setSelectedIndex(0);
    };

    return (
        <div className={className}>
            <input
                id={galleryInputId}
                ref={galleryInputRef}
                type="file"
                accept={ACCEPTED_IMAGE_TYPES.join(',')}
                className="hidden"
                onChange={handleFileChange}
            />
            <input
                id={cameraInputId}
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleFileChange}
            />

            <div className="space-y-3">
                <div className="relative overflow-hidden rounded-[1.75rem] border border-stone-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/60">
                    {activePreviewUrl ? (
                        <img src={activePreviewUrl} alt="" className="w-full h-44 object-cover" />
                    ) : (
                        <div className="w-full h-44 flex flex-col items-center justify-center gap-2 text-slate-400 dark:text-slate-500">
                            <CameraIcon className="w-10 h-10 opacity-60" />
                            <span className="text-xs font-bold uppercase tracking-widest">{t('recipePhoto')}</span>
                        </div>
                    )}
                    {isUploading && (
                        <div className="absolute inset-0 bg-white/70 dark:bg-slate-900/70 flex items-center justify-center">
                            <span className="text-xs font-black uppercase tracking-widest text-brand-sage">{t('recipePhotoUploading')}</span>
                        </div>
                    )}
                    {hasImages && resolvedGalleryUrls.length > 1 && (
                        <span className="absolute bottom-3 end-3 px-2.5 py-1 rounded-full bg-black/55 text-white text-[10px] font-black uppercase tracking-wider">
                            {selectedIndex + 1}/{resolvedGalleryUrls.length}
                        </span>
                    )}
                </div>

                {resolvedGalleryUrls.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1">
                        {resolvedGalleryUrls.map((url, index) => (
                            <button
                                key={`${url}-${index}`}
                                type="button"
                                onClick={() => setSelectedIndex(index)}
                                className={`relative shrink-0 w-16 h-16 rounded-2xl overflow-hidden border-2 transition-all ${index === selectedIndex ? 'border-brand-sage' : 'border-stone-100 dark:border-slate-700 opacity-75 hover:opacity-100'}`}
                            >
                                <img src={url} alt="" className="w-full h-full object-cover" />
                            </button>
                        ))}
                    </div>
                )}

                <div className="flex flex-wrap items-center justify-center gap-2">
                    <button
                        type="button"
                        onClick={openSourceModal}
                        disabled={disabled || isUploading}
                        className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-brand-sage text-white text-[11px] font-black uppercase tracking-wider shadow-md disabled:opacity-50"
                    >
                        <CameraIcon className="w-4 h-4" />
                        {hasImages ? t('recipePhotoGalleryAdd') : t('recipePhotoAdd')}
                    </button>
                    {hasImages && (onRemoveAtIndex || onClear) && (
                        <button
                            type="button"
                            onClick={handleRemoveSelected}
                            disabled={disabled || isUploading}
                            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white dark:bg-slate-900 text-rose-600 text-[11px] font-black uppercase tracking-wider shadow-md border border-rose-100 dark:border-rose-900/40 disabled:opacity-50"
                        >
                            <TrashIcon className="w-4 h-4" />
                            {t('recipePhotoRemove')}
                        </button>
                    )}
                </div>

                {localError && !isSourceModalOpen && (
                    <p className="text-xs font-bold text-rose-600 text-center">{localError}</p>
                )}
            </div>

            {isSourceModalOpen && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-[700] p-4 animate-fade-in"
                    onClick={closeSourceModal}
                >
                    <div
                        className="bg-white dark:bg-slate-900 rounded-t-[2rem] sm:rounded-[2rem] shadow-2xl w-full max-w-sm overflow-hidden border border-white/20 dark:border-slate-800"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800">
                            <h3 className="text-base font-black text-slate-800 dark:text-slate-100">
                                {sourceModalStep === 'url' ? t('recipePhotoFromUrl') : t('recipePhotoSourceTitle')}
                            </h3>
                            <button
                                type="button"
                                onClick={closeSourceModal}
                                className="p-2 rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                                aria-label={t('close')}
                            >
                                <XIcon className="w-5 h-5" />
                            </button>
                        </div>

                        {sourceModalStep === 'choices' ? (
                            <div className="p-4 space-y-2">
                                <button
                                    type="button"
                                    onClick={openUrlStep}
                                    className="w-full flex items-center gap-3 p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/70 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-start"
                                >
                                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-100 dark:bg-sky-900/40 text-sky-600 dark:text-sky-400">
                                        <GlobeIcon className="w-5 h-5" />
                                    </span>
                                    <span className="text-sm font-bold text-slate-800 dark:text-slate-100">{t('recipePhotoFromUrl')}</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={openGallery}
                                    className="w-full flex items-center gap-3 p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/70 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-start"
                                >
                                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-sage/15 text-brand-sage">
                                        <CameraIcon className="w-5 h-5" />
                                    </span>
                                    <span className="text-sm font-bold text-slate-800 dark:text-slate-100">{t('recipePhotoFromDevice')}</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={openCamera}
                                    className="w-full flex items-center gap-3 p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/70 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-start"
                                >
                                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-100 dark:bg-orange-900/30 text-brand-orange">
                                        <CameraIcon className="w-5 h-5" />
                                    </span>
                                    <span className="text-sm font-bold text-slate-800 dark:text-slate-100">{t('recipePhotoFromCamera')}</span>
                                </button>
                            </div>
                        ) : (
                            <div className="p-6 space-y-4">
                                <input
                                    type="url"
                                    value={urlDraft}
                                    onChange={(event) => setUrlDraft(event.target.value)}
                                    placeholder={t('recipePhotoUrlPlaceholder')}
                                    className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm font-medium focus:ring-2 focus:ring-brand-sage outline-none"
                                    autoFocus
                                />
                                {localError && (
                                    <p className="text-xs font-bold text-rose-600">{localError}</p>
                                )}
                                <div className="flex gap-3">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSourceModalStep('choices');
                                            setLocalError(null);
                                        }}
                                        className="flex-1 py-3 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-black rounded-xl text-xs uppercase tracking-widest"
                                    >
                                        {t('cancel')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={confirmUrl}
                                        className="flex-1 py-3 bg-brand-sage text-white font-black rounded-xl text-xs uppercase tracking-widest"
                                    >
                                        {t('ok')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default RecipeImagePicker;
